
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Globe } from "lucide-react";
import { MockBadge } from "@/components/ui/mock-badge";

interface GeoData {
  country: string;
  count: number;
}

interface GeographicAnalyticsProps {
  geoData?: GeoData[];
  isLoading: boolean;
}

export function GeographicAnalytics({ geoData, isLoading }: GeographicAnalyticsProps) {
  // Mock data for development until phase 4 integration is complete
  const mockGeoData = [
    { country: "United States", count: 42 },
    { country: "United Kingdom", count: 18 },
    { country: "Germany", count: 12 },
    { country: "Canada", count: 10 },
    { country: "Australia", count: 8 },
    { country: "France", count: 6 },
    { country: "Other", count: 4 },
  ];

  const data = geoData || mockGeoData;
  
  const chartData = {
    labels: data.map(item => item.country),
    datasets: [
      {
        label: 'Opens by Country',
        data: data.map(item => item.count),
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        borderColor: 'rgb(75, 192, 192)',
        borderWidth: 1,
      },
    ],
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-medium">Geographic Distribution</CardTitle>
            <CardDescription>Where your subscribers are opening emails</CardDescription>
          </div>
          <div className="flex gap-2">
            <MockBadge 
              phase="4" 
              tooltip="Geographic analytics currently displays mock data. Actual location tracking will be implemented in Phase 4."
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="w-full h-[250px]" />
        ) : (
          <div className="space-y-4">
            <div className="h-[250px]">
              <BarChart data={chartData} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
              {data.slice(0, 4).map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 border rounded-md">
                  <Globe className="h-4 w-4 text-teal-500" />
                  <div>
                    <p className="text-xs font-medium">{item.country}</p>
                    <p className="text-xs text-gray-500">{item.count}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
